import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePermitBenchmarkingURLState } from '@/hooks/usePermitBenchmarkingURLState';
import { updateURLState } from '@/utils/urlParams';
import { Place } from '@/contexts/PlaceNamesContext';
import { HeroSection } from './components/HeroSection';
import { StepSection } from './components/StepSection';
import { TabsSection } from './components/TabsSection';
import { ChartsSection } from './components/ChartsSection';
import { JurisdictionSnapshotSection } from './components/JurisdictionSnapshotSection';
import { OverTimeView } from './components/OverTimeView';
import { usePeerCities } from '@/hooks/usePeerCities';
import { useConsolidatedMetrics } from '@/hooks/useConsolidatedMetrics';
import { formatPlaceName } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { ProcessedMetrics } from '@/types';
import { supabase } from '@/lib/supabase';
import { calcLatestValueAndGrowth } from '@/utils/metrics';
import { useSavedBenchmarkComparison } from '@/hooks/useSavedBenchmarkComparison';
import { Loader2 } from 'lucide-react';
import { TAB_IDS, TabId } from './types/data';

export const NewBenchmarksUI: React.FC = () => {
  // ----- URL state ----- //
  const [, setSearchParams] = useSearchParams();
  const { selectedPlace: urlPlace, peers: urlPeers, activeTab: urlTab } = usePermitBenchmarkingURLState();
  const hasUrlState = !!urlPlace || (urlPeers && urlPeers.length > 0);

  const [selectedPlace, setSelectedPlace] = useState<Place | null>(urlPlace || null);
  const [displayName, setDisplayName] = useState<string>('');
  const [peerMetrics, setPeerMetrics] = useState<{ peer: Place; data: ProcessedMetrics[] }[]>([]);
  const [loadingPeerMetrics, setLoadingPeerMetrics] = useState(false);
  const [peerMetricsError, setPeerMetricsError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>(urlTab || TAB_IDS.PEER_COMPARISON);

  // Track first render for URL syncing
  const isInitialRender = useRef(true);

  // Load saved comparison (may be overridden by URL params)
  const { data: savedComparison, loading: loadingSaved, error: savedError } = useSavedBenchmarkComparison();

  // ---------------- Data precedence ---------------- //
  // 1) URL params (shared link) win over saved comparison.
  // 2) If no URL params, fall back to saved comparison.

  // No need for separate effect; initial state already honours urlPlace.

  // Get peer cities, initialise with either URL peers or saved peers (later decided)
  
  const {
    peerCities,
    loadingPeers,
    peersError,
    addPeer,
    removePeer,
    replacePeers,
  } = usePeerCities({
    selectedPlace,
    initialPeers: hasUrlState ? urlPeers : (savedComparison?.peerPlaces ?? []),
  });

  // --- NEW: Wrap removePeer to update URL immediately ---
  const handleRemovePeer = useCallback((placeFips: string) => {
    removePeer(placeFips);
    // Update the URL state after peer removal
    updateURLState(setSearchParams, selectedPlace, peerCities.filter((p) => p.place_fips !== placeFips), activeTab);
  }, [removePeer, setSearchParams, selectedPlace, peerCities, activeTab]);

  // Apply saved comparison (only if no URL override)
  useEffect(() => {
    if (!savedComparison || hasUrlState) return;

    // Set place if not already set
    if (!selectedPlace && savedComparison.selectedPlace) {
      setSelectedPlace(savedComparison.selectedPlace);
    }

    // Replace peers with saved peers
    if (savedComparison.peerPlaces?.length) {
      replacePeers(savedComparison.peerPlaces);
    }

    // Active tab from saved comparison (only if no URL tab)
    if (!urlTab) {
      setActiveTab((savedComparison.activeTab as TabId) || TAB_IDS.PEER_COMPARISON);
    }
  }, [savedComparison, hasUrlState, selectedPlace, replacePeers, urlTab]);

  // ---------------- URL sync ---------------- //
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return; // Do not overwrite the URL we just parsed
    }
    updateURLState(setSearchParams, selectedPlace, peerCities, activeTab);
  }, [selectedPlace, peerCities, activeTab, setSearchParams]);

  // Get the selected place metrics
  const { data: selectedPlaceMetrics, loading: metricsLoading, error: metricsError } = useConsolidatedMetrics(
    selectedPlace?.name || null,
    selectedPlace?.place_fips || null
  );

  const fetchPeerMetrics = useCallback(async () => {
    if (!peerCities.length) {
      setPeerMetrics([]);
      setLoadingPeerMetrics(false);
      setPeerMetricsError(null);
      return;
    }

    setLoadingPeerMetrics(true);
    setPeerMetricsError(null);

    try {
      const peerResults = await Promise.all(
        peerCities.map(async (peer) => {
          const { data: supabaseData, error: supabaseError } = await supabase
            .from('consolidated_jurisdiction_metrics')
            .select('*')
            .eq('Place', peer.name)
            .eq('place_fips', peer.place_fips)
            .order('year', { ascending: false })
            .limit(6);  // Get 6 years of data for proper growth calculations

          if (supabaseError) {
            throw new Error(`Error fetching data for ${peer.name}: ${supabaseError.message}`);
          }

          if (!supabaseData || supabaseData.length === 0) {
            return { peer, data: [] };
          }

          const processedData = supabaseData.map(record => ({
            ...record,
            population_metrics: calcLatestValueAndGrowth(supabaseData, 'total_population'),
            housing_units_metrics: calcLatestValueAndGrowth(supabaseData, 'housing_units_per_1000_residents'),
            price_to_income_metrics: calcLatestValueAndGrowth(supabaseData, 'price_to_income'),
            permits_metrics: calcLatestValueAndGrowth(supabaseData, 'bps_total_units'),
          }));

          return { peer, data: processedData };
        })
      );

      setPeerMetrics(peerResults);
      setLoadingPeerMetrics(false);
    } catch (error) {
      console.error('Error fetching peer metrics:', error);
      setPeerMetricsError(error instanceof Error ? error.message : 'Failed to fetch peer metrics');
      setPeerMetrics([]);
      setLoadingPeerMetrics(false);
    }
  }, [peerCities]);

  // Handler for hero search selection
  const handlePlaceSelect = (place: Place) => {
    setSelectedPlace(place);
    // When a new place is selected from the hero search,
    // we should clear the existing peers to trigger a new fetch.
    replacePeers([]);
  };

  // Effect to fetch peer metrics when peers change
  useEffect(() => {
    fetchPeerMetrics();
  }, [fetchPeerMetrics]);

  // Effect to update display name when selected place changes
  useEffect(() => {
    if (selectedPlace) {
      setDisplayName(formatPlaceName(selectedPlace.name));
    }
  }, [selectedPlace]);

  // Prepare metrics data for saving
  const metricsData = {
    selectedPlaceMetrics,
    peerMetrics,
    loading: metricsLoading || loadingPeers || loadingPeerMetrics,
    error: metricsError || peersError || peerMetricsError,
  };

  // Show loading state while fetching saved comparison
  if (loadingSaved) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const handleTabChange = (tabId: TabId) => {
    setActiveTab(tabId);
  };

  return (
    <div className="flex flex-col">
      <div className="flex-none">
        <HeroSection 
          selectedPlace={selectedPlace}
          onPlaceSelect={handlePlaceSelect}
          displayName={displayName}
        />
      </div>
      
      {selectedPlace && (
        <div className="flex-1">
          <TabsSection 
            selectedPlace={selectedPlace} 
            peerPlaces={peerCities}
            metricsData={metricsData}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />

          {activeTab === TAB_IDS.PEER_COMPARISON && (
            <>
              <StepSection 
                selectedPlace={selectedPlace}
                displayName={displayName}
                peers={peerCities}
                loadingPeers={loadingPeers}
                peersError={peersError}
                onAddPeer={addPeer}
                onRemovePeer={handleRemovePeer}
              />

              <ChartsSection
                selectedPlace={selectedPlace}
                selectedPlaceMetrics={selectedPlaceMetrics}
                peerMetrics={peerMetrics}
                loading={metricsLoading || loadingPeers || loadingPeerMetrics}
                error={metricsError || peersError || peerMetricsError}
              />
            </>
          )}

          {activeTab === TAB_IDS.JURISDICTION_SNAPSHOT && (
            <JurisdictionSnapshotSection
              selectedPlace={selectedPlace}
              displayName={displayName}
              peerPlaces={peerCities}
              metricsData={metricsData}
            />
          )}

          {activeTab === TAB_IDS.JURISDICTION_OVERTIME && (
            <OverTimeView selectedPlace={selectedPlace} />
          )}
        </div>
      )}
    </div>
  );
};